/**
 * The `ERR_*` catalogue — every code the API can return, and the sentence a person
 * should read instead.
 *
 * It lives in `shared` rather than in the client because the two halves have to agree
 * on the spelling: a code the server throws and the client has never heard of renders
 * as a generic failure, which is how "the price must be higher than the compare-at
 * price" becomes "something went wrong". Adding a code means adding it here in the
 * same commit — there is a test that asserts every `ERR_` literal in the API source
 * has an entry.
 *
 * Messages are written for the person who hit the problem: what happened, and what
 * resolves it. No codes, no stack traces, no blame.
 */

export const ERROR_MESSAGES: Record<string, string> = {
  /* -- Request shape ------------------------------------------------------- */
  ERR_VALIDATION: 'Some details are not correct. Please check the marked fields and try again.',
  ERR_NOT_FOUND: 'That item no longer exists. Someone may have deleted it while this page was open.',
  ERR_INTERNAL: 'Something went wrong on our side. Nothing was saved. Please try again.',
  ERR_PAYLOAD_TOO_LARGE: 'That file is larger than the 10 MB limit.',
  ERR_RATE_LIMITED: 'Too many tries in a short time. Please wait a moment and try again.',

  /* -- Authentication and access ------------------------------------------- */
  ERR_INVALID_CREDENTIALS: 'That username and password do not match an account.',
  ERR_ACCOUNT_DISABLED: 'This account has been deactivated. An administrator can restore it.',
  ERR_UNAUTHORIZED: 'Please sign in to continue.',
  ERR_TOKEN_EXPIRED: 'Your session has expired. Please sign in again.',
  ERR_FORBIDDEN: 'Your role does not have access to this.',
  ERR_READ_ONLY_ACCOUNT: 'This is a read-only account. It can view everything and change nothing.',
  ERR_LOGIN_BLOCKED: 'Too many failed sign-in attempts. Try again in a few minutes.',
  ERR_WRONG_PASSWORD: 'That is not your current password.',
  ERR_LAST_ADMIN: 'This is the last active administrator. Promote another account before changing this one.',
  ERR_CANNOT_EDIT_SELF: 'You cannot change your own role or deactivate your own account.',
  ERR_USERNAME_TAKEN: 'That username is already in use.',

  /* -- Content -------------------------------------------------------------- */
  ERR_SLUG_TAKEN: 'That web address is already used by another record. Choose a different one.',
  ERR_UNKNOWN_SECTION_TYPE: 'That section type is not one this site knows how to render.',
  ERR_SECTION_PAYLOAD: 'This section has a setting that is not valid. Check its fields.',
  ERR_PAGE_IS_SYSTEM: 'This page is part of the site structure and cannot be deleted. You can unpublish it instead.',
  ERR_CATEGORY_NESTING: 'Categories go one level deep. Choose a top-level category as the parent.',
  ERR_CATEGORY_SELF_PARENT: 'A category cannot be its own parent.',
  ERR_IN_USE: 'Something still refers to this record, so it cannot be deleted. Archive it instead.',

  /* -- Store ---------------------------------------------------------------- */
  ERR_SKU_TAKEN: 'That item code already belongs to another product.',
  ERR_PRODUCT_UNAVAILABLE: 'One of the items in your cart is no longer for sale.',
  ERR_INSUFFICIENT_STOCK: 'There is not enough stock for one of the items in your cart.',
  ERR_EMPTY_CART: 'Your cart is empty.',
  ERR_INVALID_STATUS_CHANGE: 'An order cannot move to that status from where it is now.',
  ERR_ORDER_LOCKED: 'This order has been completed or cancelled and can no longer be edited.',
  ERR_NEGATIVE_STOCK: 'That movement would take stock below zero. Post an adjustment with a reason instead.',
  ERR_INVALID_MONEY: 'That is not a valid amount.',
  ERR_INVALID_QUANTITY: 'Quantities must be whole numbers.',
  ERR_INVALID_PERCENT: 'That is not a valid percentage.',

  /* -- Media ---------------------------------------------------------------- */
  ERR_UNSUPPORTED_FILE_TYPE: 'That file type is not accepted. Use JPEG, PNG, WebP, SVG or PDF.',
  ERR_UPLOAD_FAILED: 'The file could not be saved. Try again, or use a smaller file.',
  ERR_MEDIA_IN_USE: 'This file is still used somewhere on the site and was not deleted.',

  /* -- Email ---------------------------------------------------------------- */
  ERR_EMAIL_NOT_CONFIGURED: 'Email has not been set up yet. Add the SMTP details in Settings → Email.',
  ERR_EMAIL_SEND_FAILED: 'The message could not be sent. The error from the mail server is on the Email log screen.',

  /* -- Configuration -------------------------------------------------------- */
  ERR_DB_UNAVAILABLE: 'The database is not reachable right now. Nothing was saved.',
  ERR_MAINTENANCE: 'The site is in maintenance mode. Only administrators can sign in.',
};

/**
 * Turn anything a `catch` block might hold into a sentence.
 *
 * Accepts an axios-shaped error, a bare `ERR_*` string, an `Error`, or an unknown —
 * because the call site is a toast handler and should not have to know which it got.
 */
export function friendlyError(err: unknown, fallback = ERROR_MESSAGES.ERR_INTERNAL!): string {
  const code = extractErrorCode(err);
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code]!;
  return fallback;
}

/** Pull the `ERR_*` code out, or null when there isn't one. */
export function extractErrorCode(err: unknown): string | null {
  if (typeof err === 'string') return /^ERR_[A-Z0-9_]+$/.test(err) ? err : null;

  if (err && typeof err === 'object') {
    const anyErr = err as {
      response?: { data?: { error?: unknown } };
      error?: unknown;
      message?: unknown;
      code?: unknown;
    };

    const candidates = [
      anyErr.response?.data?.error,
      anyErr.error,
      anyErr.code,
      anyErr.message,
    ];
    for (const c of candidates) {
      if (typeof c === 'string' && /^ERR_[A-Z0-9_]+$/.test(c)) return c;
    }
  }
  return null;
}

/**
 * Throw an `ERR_*` carrying an HTTP status the error handler will honour.
 *
 * The status rides on the error object rather than being inferred from the code,
 * because the same code is a 404 in one place and a 409 in another, and a lookup table
 * mapping code → status is one more thing to keep in step.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly details?: unknown;

  constructor(code: string, status = 400, details?: unknown) {
    super(code);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

export const notFound = (details?: unknown) => new ApiError('ERR_NOT_FOUND', 404, details);
export const forbidden = (code = 'ERR_FORBIDDEN') => new ApiError(code, 403);
export const unauthorized = (code = 'ERR_UNAUTHORIZED') => new ApiError(code, 401);
export const conflict = (code: string, details?: unknown) => new ApiError(code, 409, details);
export const badRequest = (code: string, details?: unknown) => new ApiError(code, 400, details);
